import React, { useEffect, useState, useRef } from 'react'
import { useData } from '../../Context/DataContext'
import { useAuth } from '../../Context/AuthContext'
import { useIntake } from '../../Context/IntakeContext'
import { AlertCircle, CheckCircle, X, User, Shield, Phone, Calendar, FileText, Search, ChevronDown, Loader } from 'react-feather'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

// The actual data structure after reformatInsurance transforms it in DataContext
interface InsuranceOption {
  label: string
  value: number
}

// Type assertion helper for the transformed insurance options
type TransformedInsuranceOption = InsuranceOption

// Modern input component - defined outside to prevent recreation on each render
const ModernInput = ({
  label,
  value,
  onChange,
  placeholder,
  icon: Icon,
  type = 'text',
  required = false,
  maxLength,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder: string
  icon?: React.ComponentType<any>
  type?: string
  required?: boolean
  maxLength?: number
}) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
      {label}
      {required && <span className="text-red-400">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`w-full ${Icon ? 'pl-10' : 'pl-3'} pr-3 py-2.5 bg-dark-input border border-dark-border rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-accent-teal/50 focus:ring-1 focus:ring-accent-teal/20 transition-all`}
      />
    </div>
  </div>
)

interface IntakeFormProps {
  onSuccess?: () => void
}

const formatPhone = (raw: string): string => {
  const d = raw.replace(/\D/g, '').slice(-10)
  if (d.length < 4) return d
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

const phoneDigits = (s: string): string => s.replace(/\D/g, '').slice(-10)

const IntakeForm: React.FC<IntakeFormProps> = ({ onSuccess }) => {
  const { currentProfile } = useAuth()
  const { insuranceOptions } = useData()
  const { addIntakeRecord, loadingNewIntake } = useIntake()

  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [dateOfBirth, setDateOfBirth] = useState<Date>(new Date())
  const [policy, setPolicy] = useState<string>('')
  const [insurance, setInsurance] = useState<string>('')
  const [payerId, setPayerId] = useState<string>('')
  const [source, setSource] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [emergencyPhone, setEmergencyPhone] = useState<string>('')
  const [emergencyName, setEmergencyName] = useState<string>('')

  const [insuranceOpen, setInsuranceOpen] = useState<boolean>(false)
  const [insuranceSearch, setInsuranceSearch] = useState<string>('')
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [showSuccess, setShowSuccess] = useState<boolean>(false)
  const [showError, setShowError] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const insuranceRef = useRef<HTMLDivElement>(null)

  // Close insurance dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (insuranceRef.current && !insuranceRef.current.contains(event.target as Node)) {
        setInsuranceOpen(false)
        setInsuranceSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Cast to actual transformed type since DataContext's reformatInsurance changes the structure
  const typedInsuranceOptions = insuranceOptions as unknown as TransformedInsuranceOption[] | null

  const filteredInsuranceOptions = typedInsuranceOptions?.filter((option) =>
    option.label?.toLowerCase()?.includes(insuranceSearch.toLowerCase())
  ) || []

  const handleSelectInsurance = (option: InsuranceOption) => {
    setInsurance(option.label)
    setPayerId(String(option.value))
    setInsuranceOpen(false)
    setInsuranceSearch('')
  }

  const addNewIntakeRecord = async () => {
    setValidationErrors([])
    setShowError(false)
    setShowSuccess(false)

    const data = {
      name: `${firstName} ${lastName}`.trim(),
      policy: policy,
      insurance: insurance,
      payerId: payerId,
      dob: dateOfBirth,
      source: source,
      userId: currentProfile.userid,
      notes: note,
      phone_contact: phoneNumber,
      emergency_contact: emergencyName,
      emergency_number: emergencyPhone,
    }

    const errors: string[] = []
    const fieldLabels: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      policy: 'Policy Number',
      insurance: 'Insurance',
      payerId: 'Insurance Provider',
      dob: 'Date of Birth',
      userId: 'User ID',
      notes: 'Initial Note',
      phone_contact: 'Phone Number'
    }

    if (!firstName) errors.push(fieldLabels.firstName)
    if (!lastName) errors.push(fieldLabels.lastName)
    if (!policy) errors.push(fieldLabels.policy)
    if (!insurance) errors.push(fieldLabels.insurance)
    if (!payerId) errors.push(fieldLabels.payerId)
    if (!dateOfBirth) errors.push(fieldLabels.dob)
    if (!note) errors.push(fieldLabels.notes)
    if (!phoneNumber) errors.push(fieldLabels.phone_contact)
    else if (phoneDigits(phoneNumber).length !== 10) errors.push(`${fieldLabels.phone_contact} (must be 10 digits)`)
    if (emergencyPhone && phoneDigits(emergencyPhone).length !== 10) errors.push('Emergency Phone (must be 10 digits)')

    if (errors.length > 0) {
      setValidationErrors(errors)
      return
    }

    try {
      await addIntakeRecord(data)
      setShowSuccess(true)
      // Reset form and close modal after short delay
      setTimeout(() => {
        setFirstName('')
        setLastName('')
        setPolicy('')
        setInsurance('')
        setPayerId('')
        setDateOfBirth(new Date())
        setSource('')
        setNote('')
        setPhoneNumber('')
        setEmergencyName('')
        setEmergencyPhone('')
        setShowSuccess(false)
        // Call onSuccess callback to close modal
        if (onSuccess) {
          onSuccess()
        }
      }, 1500)
    } catch (error) {
      setShowError(true)
      setErrorMessage('Failed to add intake record. Please try again.')
    }
  }

  return (
    <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
      {/* Client Information Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 pb-1 border-b border-dark-border">
          <User className="w-4 h-4 text-accent-teal" />
          <span className="text-sm font-medium text-white">Client Information</span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <ModernInput
            label="First Name"
            value={firstName}
            onChange={setFirstName}
            placeholder="John"
            icon={User}
            required
          />
          <ModernInput
            label="Last Name"
            value={lastName}
            onChange={setLastName}
            placeholder="Doe"
            required
          />
        </div>

        {/* Date of Birth */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
            Date of Birth
            <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 z-10" />
            <DatePicker
              selected={dateOfBirth}
              onChange={(date: Date) => setDateOfBirth(date)}
              className="w-full pl-10 pr-3 py-2.5 bg-dark-input border border-dark-border rounded-xl text-white text-sm focus:outline-none focus:border-accent-teal/50 focus:ring-1 focus:ring-accent-teal/20 transition-all"
              dateFormat="MM/dd/yyyy"
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100}
            />
          </div>
        </div>
      </div>

      {/* Insurance Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 pb-1 border-b border-dark-border">
          <Shield className="w-4 h-4 text-accent-purple" />
          <span className="text-sm font-medium text-white">Insurance Details</span>
        </div>

        {/* Insurance Dropdown */}
        <div className="space-y-1.5" ref={insuranceRef}>
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
            Insurance Provider
            <span className="text-red-400">*</span>
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setInsuranceOpen(!insuranceOpen)}
              className="w-full px-3 py-2.5 bg-dark-input border border-dark-border rounded-xl text-left flex items-center justify-between hover:border-gray-600 transition-colors"
            >
              <span className={insurance ? 'text-white text-sm' : 'text-gray-500 text-sm'}>
                {insurance || 'Select insurance...'}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${insuranceOpen ? 'rotate-180' : ''}`} />
            </button>

            {insuranceOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-dark-card border border-dark-border rounded-xl shadow-xl z-50 overflow-hidden">
                {/* Search */}
                <div className="p-2 border-b border-dark-border">
                  <div className="flex items-center gap-2 px-3 py-2 bg-dark-input rounded-lg">
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={insuranceSearch}
                      onChange={(e) => setInsuranceSearch(e.target.value)}
                      placeholder="Search insurance..."
                      className="bg-transparent text-white text-sm placeholder-gray-500 focus:outline-none flex-1"
                      autoFocus
                    />
                  </div>
                </div>
                {/* Options */}
                <div className="max-h-40 overflow-y-auto">
                  {filteredInsuranceOptions.length > 0 ? (
                    filteredInsuranceOptions.map((option: InsuranceOption) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleSelectInsurance(option)}
                        className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-dark-card-hover hover:text-white transition-colors"
                      >
                        {option.label}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">No results found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <ModernInput
          label="Policy Number"
          value={policy}
          onChange={setPolicy}
          placeholder="ABC123456789"
          icon={FileText}
          required
        />
      </div>

      {/* Contact Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 pb-1 border-b border-dark-border">
          <Phone className="w-4 h-4 text-accent-gold" />
          <span className="text-sm font-medium text-white">Contact Information</span>
        </div>

        <ModernInput
          label="Phone Number"
          value={phoneNumber}
          onChange={(v) => setPhoneNumber(formatPhone(v))}
          placeholder="(555) 123-4567"
          icon={Phone}
          type="tel"
          required
          maxLength={14}
        />

        <ModernInput
          label="Source"
          value={source}
          onChange={setSource}
          placeholder="Referral source..."
        />

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1">
            Initial Note
            <span className="text-red-400">*</span>
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add initial notes..."
            rows={2}
            className="w-full px-3 py-2.5 bg-dark-input border border-dark-border rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-accent-teal/50 focus:ring-1 focus:ring-accent-teal/20 transition-all resize-none"
          />
        </div>
      </div>

      {/* Optional Fields */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 italic">Optional Fields</span>
          <div className="flex-1 h-px bg-dark-border"></div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <ModernInput
            label="Emergency Contact"
            value={emergencyName}
            onChange={setEmergencyName}
            placeholder="Contact name"
          />
          <ModernInput
            label="Emergency Phone"
            value={emergencyPhone}
            onChange={(v) => setEmergencyPhone(formatPhone(v))}
            placeholder="(555) 123-4567"
            type="tel"
            maxLength={14}
          />
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="button"
        onClick={addNewIntakeRecord}
        disabled={loadingNewIntake}
        className="w-full py-3 bg-gradient-to-r from-accent-teal to-accent-deep-purple text-white font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loadingNewIntake ? (
          <>
            <Loader className="w-4 h-4 animate-spin" />
            <span>Submitting...</span>
          </>
        ) : (
          <span>Submit Record</span>
        )}
      </button>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5 w-4 h-4" />
            <div className="flex-1">
              <h3 className="text-red-400 font-semibold text-sm mb-1">Missing Required Fields</h3>
              <ul className="text-red-400/80 text-xs space-y-0.5">
                {validationErrors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              onClick={() => setValidationErrors([])}
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSuccess && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3">
          <div className="flex items-center gap-3">
            <CheckCircle className="text-green-400 flex-shrink-0 w-4 h-4" />
            <p className="text-green-400 font-semibold text-sm flex-1">Record added successfully!</p>
            <button
              type="button"
              onClick={() => setShowSuccess(false)}
              className="text-green-400 hover:text-green-300 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {showError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
          <div className="flex items-center gap-3">
            <AlertCircle className="text-red-400 flex-shrink-0 w-4 h-4" />
            <p className="text-red-400 font-semibold text-sm flex-1">{errorMessage}</p>
            <button
              type="button"
              onClick={() => setShowError(false)}
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </form>
  )
}

export default IntakeForm
